import { LIMITS, createImageLayer, createShapeLayer, validateDocument } from '../features/ArtEditor/headless';
import { COMMUNITY_IMAGES, communityCandidates, fold, rankByWords } from './communityImages';
import ptEditor from '../../../i18n/locales/pt/artEditor.json';
import enEditor from '../../../i18n/locales/en/artEditor.json';
import esEditor from '../../../i18n/locales/es/artEditor.json';

// The image question of an art run (contract 2.9.0, ARTS_CONTRACT.md) as the double plays core's
// images phase, in simplified form: the places of a draft that want an image, how a chosen
// image goes into each one, and the copy the run writes. Core's AI proposes each subject and
// mode; here the subject comes from the page's headline (core's fallback for a template photo
// the AI did not mention) and the mode from the words in it.

// `studio.usage.image_ceilings` and `image_minimums` (credits per image, by mode). The double
// charges `IMAGE_COSTS` for each image it makes, below the ceiling as a metered cost would be.
export const IMAGE_CEILINGS = { flare: 50, sunburst: 220 };
export const IMAGE_MINIMUMS = { flare: 20, sunburst: 60 };
export const IMAGE_COSTS = { flare: 18, sunburst: 84 };
export const IMAGE_SOURCES = ['generate', 'asset', 'community', 'keep'];
// `studio.images.max_per_run` and `studio.images.question_ttl_hours`.
export const IMAGE_PLACE_LIMIT = 10;
export const IMAGE_QUESTION_TTL_MS = 24 * 3600e3;

const ASKS = /\b(imag(?:em|ens?|es?)|fotos?|fotografias?|ilustra\w*|photos?|pictures?|im[aá]genes?)\b/i;
const BACKGROUND = /\b(fundo|background|fondo)\b/i;
// A prompt or chat edit that asks for images ("Adicione imagens demonstrando cada exercício").
export const asksForImages = text => ASKS.test(text || '');
// Product rule: `sunburst` for any person or body part and for fine detail; `flare` otherwise.
const DETAILED = /exerc|treino|agach|prancha|flexao|alonga|ioga|yoga|corrid|correr|pessoa|mulher|homem|crianca|corpo|\bmaos?\b|rosto|atleta|paciente|rotulo|workout|squat|plank|stretch|runn|person|people|woman|women|\b(man|men)\b|\bbody\b|\bhands?\b|\bfaces?\b|athlete|label|ejercicio|entreno|sentadilla|plancha|persona|mujer|hombre|cuerpo|etiqueta/;

const WORDS = {
  pt_BR: {
    question: n => (n === 1 ? 'Esta arte tem um lugar para imagem. Use uma imagem sua, escolha uma da galeria ou gere com IA.'
      : `Esta arte tem ${n} lugares para imagens. Para cada um, use uma imagem sua, escolha uma da galeria ou gere com IA.`),
    subject: title => `Foto que ilustre: ${title}`, pageSubject: n => `Foto da página ${n}`,
    tipPerson: about => `Você ${about ? `mostrando “${about}”` : 'em ação'}: corpo inteiro ou meio corpo, de frente ou de lado para a câmera, com luz natural.`,
    tipThing: about => `Fotografe você mesmo ${about ? `algo que mostre “${about}”` : 'o que este espaço pede'}: de perto, num fundo simples, com luz de janela.`,
    generated: n => (n === 1 ? 'Gerei 1 imagem com IA' : `Gerei ${n} imagens com IA`),
    asset: n => (n === 1 ? 'Usei 1 foto sua' : `Usei ${n} fotos suas`),
    community: n => (n === 1 ? 'Usei 1 imagem da comunidade' : `Usei ${n} imagens da comunidade`),
    kept: 'Mantive as imagens como estavam',
    failed: 'Uma imagem não pôde entrar na arte; o lugar ficou com a imagem que tinha.',
    background: 'Fundo', shade: 'Sombra do fundo', frameShade: 'Sombra da foto',
  },
  en_US: {
    question: n => (n === 1 ? 'This art has one place for an image. Use one of your images, pick one from the gallery or generate it with AI.'
      : `This art has ${n} places for images. For each one, use one of your images, pick one from the gallery or generate it with AI.`),
    subject: title => `Photo that illustrates: ${title}`, pageSubject: n => `Photo for page ${n}`,
    tipPerson: about => `You ${about ? `showing “${about}”` : 'in action'}: full body or waist up, facing or side-on to the camera, in natural light.`,
    tipThing: about => `Shoot ${about ? `something that shows “${about}”` : 'what this place asks for'} yourself: close up, on a plain background, in window light.`,
    generated: n => (n === 1 ? 'I generated 1 image with AI' : `I generated ${n} images with AI`),
    asset: n => (n === 1 ? 'I used 1 of your photos' : `I used ${n} of your photos`),
    community: n => (n === 1 ? 'I used 1 community image' : `I used ${n} community images`),
    kept: 'I kept the images as they were',
    failed: 'An image could not go into the art; its place kept the image it had.',
    background: 'Background', shade: 'Background shade', frameShade: 'Photo shade',
  },
  es_ES: {
    question: n => (n === 1 ? 'Este arte tiene un lugar para imagen. Usa una imagen tuya, elige una de la galería o genérala con IA.'
      : `Este arte tiene ${n} lugares para imágenes. Para cada uno, usa una imagen tuya, elige una de la galería o genérala con IA.`),
    subject: title => `Foto que ilustre: ${title}`, pageSubject: n => `Foto de la página ${n}`,
    tipPerson: about => `Tú ${about ? `mostrando “${about}”` : 'en acción'}: cuerpo entero o medio cuerpo, de frente o de lado a la cámara, con luz natural.`,
    tipThing: about => `Fotografía tú mismo ${about ? `algo que muestre “${about}”` : 'lo que pide este lugar'}: de cerca, sobre un fondo sencillo, con luz de ventana.`,
    generated: n => (n === 1 ? 'Generé 1 imagen con IA' : `Generé ${n} imágenes con IA`),
    asset: n => (n === 1 ? 'Usé 1 foto tuya' : `Usé ${n} fotos tuyas`),
    community: n => (n === 1 ? 'Usé 1 imagen de la comunidad' : `Usé ${n} imágenes de la comunidad`),
    kept: 'Mantuve las imágenes como estaban',
    failed: 'Una imagen no pudo entrar en el arte; su lugar quedó con la imagen que tenía.',
    background: 'Fondo', shade: 'Sombra del fondo', frameShade: 'Sombra de la foto',
  },
};
export const imageWords = locale => WORDS[locale] || WORDS.pt_BR;
// What the run's summary adds: "Gerei 2 imagens com IA", "Usei 1 foto sua"…
export const imageNotes = (locale, counts) => ['generated', 'asset', 'community'].filter(key => counts[key]).map(key => imageWords(locale)[key](counts[key]));

const TITLES = ['headline', 'quote', 'stat', 'subheadline', 'body'];
const titleOf = page => (TITLES.map(binding => page.layers.find(layer => layer.type === 'text' && layer.binding === binding && layer.text?.trim())).find(Boolean)
  || page.layers.find(layer => layer.type === 'text' && layer.text?.trim()))?.text || '';
const area = layer => layer.width * layer.height;
const covers = (layer, doc) => layer.x <= 0 && layer.y <= 0 && layer.x + layer.width >= doc.width && layer.y + layer.height >= doc.height;
const near = (a, b) => Math.abs((a || 0) - (b || 0)) < 1;
// A template photo still holding the template's example, or an empty place (2.10.0): an image layer, or a shape filled with it.
const placeholderPhoto = layer => layer.type === 'image' && layer.binding === 'photo' && ['library', 'placeholder'].includes(layer.source?.kind);
const placeholderFrame = layer => layer.type === 'shape' && layer.binding === 'photo' && layer.fill?.type === 'image' && ['library', 'placeholder'].includes(layer.fill.source?.kind);
// A decoration painted with a black gradient: the shade over a photo, never a place for one
// (core's `ArtImagePlacement::isShade`).
export const isShade = layer => layer?.type === 'shape' && layer.binding === 'decoration' && ['linear', 'radial'].includes(layer.fill?.type)
  && (layer.fill.stops || []).every(stop => String(stop.color || '').toLowerCase() === '#000000');
// The shade over `frame`: same shape and box (±1), black gradient (core's `isShadeOf`).
export const isShadeOf = (layer, frame) => isShade(layer) && layer.shape === frame?.shape
  && near(layer.x, frame.x) && near(layer.y, frame.y) && near(layer.width, frame.width) && near(layer.height, frame.height);
// What a layer of `page` stands for when the action is about its photo: the shade right above an
// image-filled frame is that frame (it takes the clicks, being on top); any other layer is itself.
export function photoFrameOf(page, layer) {
  if (!page || !isShade(layer)) return layer;
  const below = page.layers[page.layers.findIndex(item => item.id === layer.id) - 1];
  return below?.type === 'shape' && below.fill?.type === 'image' && isShadeOf(layer, below) ? below : layer;
}
// A shape that can take a photo as its fill (core's `ArtImagePlanner::framable`): visible, not a
// line, an arrow or a shade, a tenth of the page at least, not the whole page, not already
// showing the creator's image.
const framable = (layer, doc) => layer.type === 'shape' && !['line', 'arrow'].includes(layer.shape) && layer.visible !== false && !isShade(layer)
  && layer.fill?.source?.kind !== 'asset' && area(layer) >= 0.1 * doc.width * doc.height && !covers(layer, doc);
const box = (width, height) => ({ width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) });
// The words the editor's "Gerar com IA" writes before the creator's own (artEditor
// `generateImagePrompt`): what follows them is what the image should show.
const ASK_PREFIXES = [ptEditor, enEditor, esEditor].map(words => fold(words.generateImagePrompt || '').trim()).filter(Boolean);
const askedSubject = prompt => {
  const text = (prompt || '').normalize('NFC').replace(/\s+/g, ' ').trim();
  const prefix = ASK_PREFIXES.find(item => fold(text).startsWith(item));
  return prefix ? text.slice(prefix.length).trim().slice(0, 300) : '';
};

// A chat edit scoped to some layers (`scope`, the run's `target_layer_ids`) asks only about
// them, wherever they are: an image layer is a `slot` (even one holding the creator's photo),
// a shape a `frame` (a shade standing for the frame it covers), never a background.
function scopedPlaces(document, scope, touched) {
  const wanted = new Set(scope);
  const seen = new Set();
  const found = [];
  document.pages.forEach((page, index) => page.layers.forEach(item => {
    if (!wanted.has(item.id)) return;
    const layer = photoFrameOf(page, item);
    const fits = layer.type === 'image' || (layer.type === 'shape' && !['line', 'arrow'].includes(layer.shape) && !isShade(layer));
    if (!fits || touched.has(layer.id) || seen.has(layer.id)) return;
    seen.add(layer.id);
    found.push({ layer, placement: layer.type === 'image' ? 'slot' : 'frame', placeholder: placeholderPhoto(layer) || placeholderFrame(layer), page, index });
  }));
  return found;
}

// The places of a draft that want an image, in page order, at most 10: on a new layout every
// template photo (`slot` for an image layer, `frame` for a shape filled with it); when the
// prompt asks for images and brings none, each page without one (the closing page of a
// carousel aside) gets its template photo, else its largest framable shape, else its
// background (the background first when the prompt asks for one), or, for an edit scoped to
// some layers (`scope`), those layers alone. A layer this run already changed (an attached
// photo) is never a place, nor a background holding the creator's image. `placeholder` marks
// a template photo: its default answer keeps it. The subject is the page's headline, or what
// the creator wrote after the editor's "Gerar com IA" words; the tip for the creator's own
// photo (2.12.0 `photo_tip`, which core's AI writes) is built from the same words, by mode.
export function imagePlaces(document, { prompt = '', newLayout = true, changed = [], attached = false, locale = 'pt_BR', scope = null } = {}) {
  const words = imageWords(locale);
  const touched = new Set(changed);
  const asked = asksForImages(prompt) && !attached;
  const backdrop = asked && BACKGROUND.test(prompt);
  const scoped = !!scope?.length;
  const typed = scoped && asked ? askedSubject(prompt) : '';
  const found = scoped ? (asked ? scopedPlaces(document, scope, touched) : []) : [];
  if (!scoped) document.pages.forEach((page, index) => {
    const free = page.layers.filter(layer => !touched.has(layer.id));
    let own = newLayout ? free.filter(layer => placeholderPhoto(layer) || placeholderFrame(layer))
      .map(layer => ({ layer, placement: layer.type === 'image' ? 'slot' : 'frame', placeholder: true })) : [];
    const closing = document.pages.length > 1 && index === document.pages.length - 1;
    if (asked && !own.length && !closing) {
      const photo = free.find(placeholderPhoto);
      const frame = free.filter(layer => framable(layer, document)).sort((a, b) => area(b) - area(a))[0];
      const mine = page.layers.some(layer => layer.type === 'image' && layer.source?.kind === 'asset' && covers(layer, document));
      if ((photo || frame) && !backdrop) own = [{ layer: photo || frame, placement: photo ? 'slot' : 'frame', placeholder: false }];
      else if (!mine) own = [{ layer: null, placement: 'background', placeholder: false }];
    }
    own.forEach(item => found.push({ ...item, page, index }));
  });
  return found.slice(0, IMAGE_PLACE_LIMIT).map(({ layer, placement, placeholder, page, index }, position) => {
    const title = titleOf(page).replace(/\s+/g, ' ').trim();
    const subject = (typed || (title ? words.subject(title) : words.pageSubject(index + 1))).slice(0, 300);
    const bleed = page.layers.find(item => item.type === 'image' && covers(item, document));
    const current = placement === 'slot' ? layer.source : placement === 'frame' ? (layer.fill?.type === 'image' ? layer.fill.source : null) : bleed?.source || null;
    const mode = DETAILED.test(fold(`${subject} ${asked ? prompt : ''}`)) ? 'sunburst' : 'flare';
    const named = typed || title;
    const about = named.length > 80 ? `${named.slice(0, 80).replace(/\s+\S*$/, '')}…` : named;
    return { slot_id: `img-${position + 1}`, page: index + 1, page_id: page.id, layer_id: layer?.id || null, placement, subject,
      photo_tip: (mode === 'sunburst' ? words.tipPerson(about) : words.tipThing(about)).slice(0, 240), mode,
      box: layer ? box(layer.width, layer.height) : box(document.width, document.height), current: current ? { ...current } : null, placeholder };
  });
}

const SHADE = { type: 'linear', angle: 180, stops: [{ offset: 0, color: '#000000', opacity: 0.1 }, { offset: 1, color: '#000000', opacity: 0.78 }] };
const NEUTRAL_ADJUST = { brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false };
const within = (layer, frame) => layer.x >= frame.x - 1 && layer.y >= frame.y - 1
  && layer.x + layer.width <= frame.x + frame.width + 1 && layer.y + layer.height <= frame.y + frame.height + 1;
// Plain text over a dark shade turns white; text on its own highlight keeps its colors.
const whiten = (layer, changed) => {
  if (layer.style.highlight || layer.style.color === '#ffffff') return layer;
  changed.add(layer.id);
  return { ...layer, style: { ...layer.style, color: '#ffffff' } };
};

// One chosen image into its place, as core's ArtImagePlacement does (the run's images phase,
// and the image question's preview): `slot` swaps the image layer's photo; `frame` fills the
// shape with it (when texts or icons sit inside, the texts turn white, the icons are hidden
// and one shade shape goes right above it, unless one is already there or the page has no
// room for another layer, which leaves the texts as they are); `background` covers the
// page, with a shade. null when the place is gone or the result would not be a valid
// document (the caller warns instead).
export function placeImage(document, place, source, locale = 'pt_BR') {
  const words = imageWords(locale);
  const index = document.pages.findIndex(page => page.id === place.page_id);
  if (index < 0) return null;
  const changed = new Set();
  let layers = document.pages[index].layers;
  if (place.placement === 'slot') {
    if (!layers.some(layer => layer.id === place.layer_id && layer.type === 'image')) return null;
    layers = layers.map(layer => (layer.id === place.layer_id ? { ...layer, source, focus_x: 0.5, focus_y: 0.5, zoom: 1 } : layer));
    changed.add(place.layer_id);
  } else if (place.placement === 'frame') {
    const at = layers.findIndex(layer => layer.id === place.layer_id && layer.type === 'shape' && layer.shape !== 'line');
    if (at < 0) return null;
    const frame = layers[at];
    const shaded = isShadeOf(layers[at + 1], frame);
    const over = layers.slice(at + 1).filter(layer => ['text', 'icon'].includes(layer.type) && layer.visible !== false && within(layer, frame));
    const inside = new Set(!over.length || (!shaded && layers.length + 1 > LIMITS.layers) ? [] : over.map(layer => layer.id));
    const filled = { ...frame, fill: { type: 'image', source, fit: 'cover', focus_x: 0.5, focus_y: 0.5, zoom: 1, adjust: frame.fill?.type === 'image' ? frame.fill.adjust : NEUTRAL_ADJUST } };
    const shade = inside.size && !shaded ? { ...createShapeLayer(document, frame.shape, { fill: SHADE, width: frame.width, height: frame.height, binding: 'decoration' }),
      x: frame.x, y: frame.y, rotation: frame.rotation, corner_radius: frame.corner_radius, name: words.frameShade } : null;
    layers = layers.flatMap((layer, position) => {
      if (position === at) return shade ? [filled, shade] : [filled];
      if (!inside.has(layer.id)) return [layer];
      if (layer.type === 'icon') { changed.add(layer.id); return [{ ...layer, visible: false }]; }
      return [whiten(layer, changed)];
    });
    changed.add(frame.id);
    if (shade) changed.add(shade.id);
  } else {
    const bleed = layers.find(layer => layer.type === 'image' && covers(layer, document));
    if (bleed) {
      layers = layers.map(layer => (layer.id === bleed.id ? { ...layer, source, focus_x: 0.5, focus_y: 0.5, zoom: 1 } : layer));
      changed.add(bleed.id);
    } else {
      const photo = { ...createImageLayer(document, source, { binding: 'photo', name: words.background }), x: 0, y: 0, width: document.width, height: document.height, locked: true };
      const shade = { ...createShapeLayer(document, 'rect', { name: words.shade, fill: SHADE }), x: 0, y: 0, width: document.width, height: document.height, locked: true };
      layers = [photo, shade, ...layers.map(layer => (layer.type === 'text' ? whiten(layer, changed) : layer))];
      changed.add(photo.id); changed.add(shade.id);
    }
  }
  const next = { ...document, pages: document.pages.map((page, position) => (position === index ? { ...page, layers } : page)) };
  return validateDocument(next).length ? null : { document: next, changed: [...changed] };
}

// The double's image model: the community photo whose description shares the most words with
// the subject, else one picked by the subject itself, so a subject always gets the same photo.
export function sampleImage({ subject }) {
  const [best] = rankByWords(subject, communityCandidates());
  const hash = [...fold(subject)].reduce((total, char) => (total * 31 + char.charCodeAt(0)) >>> 0, 7);
  const image = best?.value || COMMUNITY_IMAGES[hash % COMMUNITY_IMAGES.length];
  return { url: image.url, width: image.width, height: image.height };
}
